import React, { useEffect, useState, useRef } from "react";
import anime from "animejs";
import { loadStripe } from "@stripe/stripe-js";
import {
    PaymentElement,
    Elements,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { useNavigate, useParams } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaTicketAlt, FaExclamationCircle, FaLock, FaCheckCircle, FaCcVisa, FaCcMastercard, FaCcAmex } from "react-icons/fa";
import { currencyFormatter } from "../../helpers/Helper";
import { getUser } from "../../helpers/AuthService";
import { PiShoppingCartDuotone, PiTicketDuotone, PiFilmScriptDuotone } from "react-icons/pi";
import { useResource } from "../../context/AppContext";
import { Drawer, DrawerHeader, DrawerItems } from "flowbite-react";

import Loading from "../../components/Loading";
import api from "../../helpers/Api";
import MessageError from "../../components/MessageError";
import { SkeletonPayment } from "../../components/Skeleton";
import moment from "moment";
import toast from "react-hot-toast";
import { verifyPaymentFlow } from "../../helpers/verifyPaymentFlow";

const getApiErrorMessage = (error, fallback = "Ocurrió un error. Intenta nuevamente.") => {
    const apiMessage = error?.response?.data?.message;
    const apiError = error?.response?.data?.error;
    const status = error?.response?.status;

    if (apiMessage) return apiMessage;

    if (apiError === "HOLD_EXPIRED") {
        return "Tu tiempo de compra terminó. Vuelve a seleccionar tus boletos para continuar.";
    }

    if (apiError === "HOLD_NOT_FOUND") {
        return "No encontramos tu reserva. Intenta seleccionar tus boletos de nuevo.";
    }

    if (apiError === "HOLD_NOT_PAYABLE") {
        return "Tu reserva ya no está disponible para pago. Intenta nuevamente desde la selección de boletos.";
    }

    if (apiError === "FORBIDDEN") {
        return "No tienes permiso para continuar con esta compra.";
    }

    if (apiError === "KEYSET_MODE_MISMATCH") {
        return "Hubo un problema al preparar el pago. Intenta nuevamente en unos momentos.";
    }

    if (apiError === "INVALID_REQUEST") {
        return "La solicitud de pago no fue válida. Recarga la página e inténtalo de nuevo.";
    }

    if (status === 500) {
        return "Tuvimos un problema interno al procesar tu pago. Intenta nuevamente en un momento.";
    }

    return fallback;
};

const getStripeFriendlyMessage = (error) => {
    if (!error) {
        return "No pudimos procesar tu pago. Intenta nuevamente.";
    }

    if (error.message) return error.message;

    if (error.code === "card_declined") {
        return "Tu tarjeta fue rechazada. Intenta con otra tarjeta o verifica los datos.";
    }

    if (error.code === "expired_card") {
        return "Tu tarjeta venció. Intenta con otra tarjeta.";
    }

    if (error.code === "incorrect_cvc") {
        return "El código de seguridad de tu tarjeta no es correcto.";
    }

    if (error.code === "processing_error") {
        return "No pudimos procesar tu tarjeta en este momento. Intenta nuevamente.";
    }

    return "No pudimos procesar tu pago. Intenta nuevamente.";
};

function PaymentProcessingModal({ message }) {
    const ringRef = useRef(null);
    const logoRef = useRef(null);
    const dotsRef = useRef([]);

    useEffect(() => {
        anime({
            targets: logoRef.current?.querySelector("span"),
            scale: ["0.8", "1.4"],
            opacity: [1, 0],
            easing: "easeOutCubic",
            duration: 1500,
            loop: true,
        });

        anime({
            targets: ringRef.current?.querySelectorAll(".pm-ring"),
            scale: [1, 1.8],
            opacity: [0.5, 0],
            easing: "easeOutCubic",
            duration: 2200,
            loop: true,
            delay: anime.stagger(550),
        });

        anime({
            targets: dotsRef.current,
            translateY: [-6, 0],
            opacity: [0.3, 1],
            easing: "easeInOutSine",
            duration: 600,
            loop: true,
            direction: "alternate",
            delay: anime.stagger(180),
        });
    }, []);

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/75 backdrop-blur-sm">
            <div className="relative flex flex-col items-center gap-7 px-10 py-10 rounded-3xl bg-[#12111a] border border-white/10 shadow-2xl w-full max-w-xs mx-4">
                <div ref={ringRef} className="relative flex items-center justify-center" style={{ width: 130, height: 130 }}>
                    <div className="pm-ring absolute rounded-full border-2 border-brand/50" style={{ inset: 0 }} />
                    <div className="pm-ring absolute rounded-full border-2 border-brand/30" style={{ inset: -14 }} />

                    <div ref={logoRef} className="img-table_wrapper loading stopped relative" style={{ width: 110, height: 110 }}>
                        <div className="absolute inset-0 backdrop-blur-md bg-white/10 rounded-full z-0" />
                        <img
                            src="/identity/MONOLOGO.png"
                            alt="Procesando"
                            style={{ width: 110, height: 110 }}
                            className="relative z-10 object-contain p-2"
                        />
                        <span className="background-gradient" />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            ref={(el) => (dotsRef.current[i] = el)}
                            className="w-2.5 h-2.5 rounded-full bg-brand"
                        />
                    ))}
                </div>

                <p className="text-white text-center font-semibold text-lg leading-snug">
                    {message || "Procesando tu pago..."}
                </p>

                <p className="text-gray-500 text-xs text-center leading-relaxed">
                    No cierres esta ventana ni<br />actualices la página
                </p>
            </div>
        </div>
    );
}

const CheckoutForm = ({ holdId, userHasLogin, remaining, paymentIdStart, paymentState, onRequireNewPI, clientSecret }) => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();

    const [errorMessage, setErrorMessage] = useState(null);
    const [loadingBuy, setLoadingBuy] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [loadingText, setLoadingText] = useState(null);
    const [skipPaymentForm, setSkipPaymentForm] = useState(false);
    const hasAutoVerifiedRef = useRef(false);

    const handleGoToTickets = (transactionId) => {
        if (!transactionId) {
            setSkipPaymentForm(false);
            setLoadingBuy(false);
            setErrorMessage("Tu pago fue confirmado, pero no pudimos abrir tus boletos automáticamente. Recarga la página o contacta soporte.");
            return;
        }

        navigate(`/tickets/${transactionId}`);
    };

    useEffect(() => {
        if (!holdId) return;
        if (!paymentIdStart) return;
        if (paymentState === "none") return;
        if (hasAutoVerifiedRef.current) return;

        hasAutoVerifiedRef.current = true;

        const ac = new AbortController();
        setSkipPaymentForm(true);
        setLoadingText("Estamos verificando si tu pago ya fue procesado...");
        setErrorMessage(null);

        verifyPaymentFlow({
            holdId,
            paymentIntentId: paymentIdStart,
            signal: ac.signal,
            onStatus: ({ message }) => setLoadingText(message),
            onCompleted: ({ transactionId }) => handleGoToTickets(transactionId),
            onFailed: ({ message, reason }) => {
                if (reason === "amex_not_allowed" || reason === "charge_refunded") onRequireNewPI?.();
                setSkipPaymentForm(false);
                setErrorMessage(
                    message ||
                    "Estamos verificando tu pago. Si ya ves el cargo en tu banco, no vuelvas a pagar; recarga la página o contacta soporte."
                );
            },
        });

        return () => ac.abort();
    }, [holdId, paymentIdStart, paymentState]);

    const handleClose = () => setIsOpen(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            if (userHasLogin) {
                const fraudAttempts = await api.get(`/IntentPayment/Fraud?id=${userHasLogin.id}`);

                if (fraudAttempts.data.customer?.is_blocked) {
                    setErrorMessage("Tu cuenta ha sido temporalmente bloqueada debido a que hemos detectado múltiples intentos de pago no válidos.");
                    setIsBlocked(true);
                    return;
                }
            }
        } catch (e) {
            setErrorMessage("No pudimos validar tu cuenta en este momento. Intenta nuevamente.");
            return;
        }

        if (!stripe || !elements) return;

        setLoadingBuy(true);
        setErrorMessage(null);
        setLoadingText("Validando tu pago...");

        const { error: submitError } = await elements.submit();
        if (submitError) {
            setLoadingBuy(false);
            setErrorMessage(getStripeFriendlyMessage(submitError));
            return;
        }

        const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({ elements });
        if (pmError) {
            setLoadingBuy(false);
            setErrorMessage(getStripeFriendlyMessage(pmError));
            return;
        }
        const cardBrand = paymentMethod?.card?.brand || "";
        if (cardBrand === "amex") {
            setLoadingBuy(false);
            setErrorMessage("Lo sentimos, no aceptamos American Express. Por favor usa Visa, Mastercard u otra tarjeta.");
            return;
        }

        const URL_ORIGIN = process.env.NODE_ENV === "development"
            ? process.env.REACT_APP_URL_DEV
            : process.env.REACT_APP_URL;

        try {
            await api.post("/IntentPayment/Attempt", { hold_id: holdId });
        } catch (e) {
            // continue to payment
        }

        const { error, paymentIntent } = await stripe.confirmPayment({
            clientSecret,
            redirect: "if_required",
            confirmParams: {
                payment_method: paymentMethod.id,
                return_url: `${URL_ORIGIN}/payment/register/${holdId}`,
            },
        });

        if (error) {
            setLoadingBuy(false);

            if (error?.code === "payment_intent_unexpected_state") {
                setSkipPaymentForm(true);
                setLoadingText("Parece que tu pago ya fue procesado. Verificando...");

                const piId = paymentIdStart;
                try {
                    await verifyPaymentFlow({
                        holdId,
                        paymentIntentId: piId,
                        onStatus: ({ message }) => setLoadingText(message),
                        onCompleted: ({ transactionId }) => handleGoToTickets(transactionId),
                        onFailed: ({ title, message, reason }) => {
                            if (reason === "amex_not_allowed" || reason === "charge_refunded") onRequireNewPI?.();
                            setSkipPaymentForm(false);
                            setErrorMessage(
                                message ||
                                title ||
                                "Tu pago podría haberse procesado. No vuelvas a pagar; recarga la página o contacta soporte."
                            );
                        },
                    });
                } finally {
                    setLoadingBuy(false);
                }
                return;
            }

            if (
                error.code === "lost_card" ||
                error.code === "stolen_card" ||
                error.code === "fraudulent" ||
                error.decline_code === "lost_card" ||
                error.decline_code === "stolen_card" ||
                error.decline_code === "fraudulent"
            ) {
                try {
                    const response = await api.post(`/IntentPayment/Fraud`, {
                        reason: error.code,
                        fraudType: "card_fraud"
                    });

                    if (response.data.blocked) {
                        setErrorMessage("Tu cuenta ha sido bloqueada por razones de seguridad. Por favor, contacta con el equipo de soporte.");
                        setIsBlocked(true);
                    } else {
                        setErrorMessage("Detectamos un problema de seguridad con la tarjeta. Intenta con otra tarjeta.");
                    }
                } catch (fraudErr) {
                    setErrorMessage("No pudimos registrar el intento de pago. Intenta con otra tarjeta o contacta soporte.");
                }

                return;
            }

            if (error.type === "card_error") {
                try {
                    const response = await api.post(`/IntentPayment/Fraud`, {
                        reason: "payment_rejected",
                        fraudType: "payment_rejected"
                    });

                    if (response.data.blocked) {
                        setErrorMessage("Tu cuenta ha sido bloqueada debido a múltiples intentos de pago rechazados. Por favor, contacta con el equipo de soporte.");
                        setIsBlocked(true);
                    } else {
                        setErrorMessage(getStripeFriendlyMessage(error));
                    }
                } catch (fraudErr) {
                    setErrorMessage(getStripeFriendlyMessage(error));
                }
            } else {
                setErrorMessage(getStripeFriendlyMessage(error));
            }

            return;
        }

        if (paymentIntent?.id) {
            try {
                await verifyPaymentFlow({
                    holdId,
                    paymentIntentId: paymentIntent.id,
                    onStatus: ({ message }) => setLoadingText(message),
                    onCompleted: ({ transactionId }) => handleGoToTickets(transactionId),
                    onFailed: ({ title, message }) => {
                        setErrorMessage(message || title || "No pudimos completar tu compra");
                    },
                });
            } finally {
                setLoadingBuy(false);
            }
        } else {
            setLoadingBuy(false);
            setLoadingText("Abriendo verificación del banco...");
        }
    };

    useEffect(() => {
        if (remaining <= 0) {
            setErrorMessage("Tu reserva expiró. Vuelve a seleccionar tus boletos e inténtalo de nuevo.");
        }
    }, [remaining]);

    return (
        <>
            <div className="text-sm items-center text-center text-gray-400 py-4">
                Al comprar aceptas nuestra <span className="text-brand hover:text-brand cursor-pointer transition-colors" onClick={() => setIsOpen(true)}>Política de Cancelación y Reembolsos</span>
            </div>

            <Drawer open={isOpen} onClose={handleClose} position="bottom">
                <DrawerHeader title="Política de Cancelación y Reembolsos" titleIcon={PiFilmScriptDuotone} />
                <DrawerItems>
                    <div className="flex-1">
                        <div className="mt-4 bg-[#292834] rounded-lg p-4 text-white text-sm">
                            <ul className="list-disc list-inside space-y-1">
                                <li>Una vez adquirido y <strong>utilizado</strong> el acceso, no se aceptarán cancelaciones ni reembolsos bajo ninguna circunstancia.</li>
                                <li>Se considera acceso utilizado cuando:
                                    <ul className="list-disc list-inside ml-4">
                                        <li>El código o boleto ha sido validado en el sistema de control de acceso del evento.</li>
                                        <li>El código único ha sido escaneado o canjeado, sin importar quién lo haya presentado.</li>
                                        <li>Existen registros en el sistema que acrediten el uso.</li>
                                    </ul>
                                </li>
                                <li>Es responsabilidad del comprador verificar la información del evento y resguardar el acceso adquirido.</li>
                                <li>En caso de cancelación o reprogramación por parte del organizador, éste definirá el procedimiento de reembolso o sustitución conforme a la legislación aplicable.</li>
                                <li>Si el acceso fue utilizado, cualquier contracargo o disputa será impugnado con evidencia de validación.</li>
                                <li>Al realizar la compra, usted declara haber leído y aceptado esta política.</li>
                            </ul>
                        </div>
                    </div>
                </DrawerItems>
            </Drawer>

            {(loadingBuy || skipPaymentForm) && (
                <PaymentProcessingModal message={loadingText || "Procesando tu pago..."} />
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                {skipPaymentForm ? null : (
                    <>
                        <PaymentElement />

                        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-gray-400 text-xs">
                            <span className="flex items-center gap-1.5">
                                <FaLock className="text-green-500" />
                                Pago 100% seguro y cifrado · Procesado por Stripe
                            </span>
                            <span className="flex items-center gap-1.5 text-lg text-gray-500">
                                <FaCcVisa aria-label="Visa" />
                                <FaCcMastercard aria-label="Mastercard" />
                                <FaCcAmex aria-label="American Express" />
                            </span>
                        </div>

                        <button
                            className={`justify-center transition-all duration-200 ${(loadingBuy || isBlocked) ? "thm-btn__disabled" : ""} event-one__btn thm-btn w-full py-3`}
                            type="submit"
                            disabled={!stripe || !elements || loadingBuy || isBlocked || remaining <= 0}
                        >
                            <span className="flex items-center justify-center">
                                <PiShoppingCartDuotone size={22} className="mr-2" />
                                {loadingBuy ? "Procesando pago..." : isBlocked ? "Pago bloqueado" : "Pagar ahora"}
                            </span>
                        </button>

                        {errorMessage && (
                            <div role="alert" aria-live="assertive" aria-atomic="true" className="text-red-600 text-center text-sm font-semibold">{errorMessage}</div>
                        )}
                    </>
                )}
            </form>
        </>
    );
};

const appearance = {
    theme: "night",
    variables: {
        colorPrimary: "#ff1e01",
        colorBackground: "#1c1b26",
        colorText: "#f5f5f5",
        colorDanger: "#ff1e01",
        borderRadius: "8px",
        fontSizeBase: "15px",
    },
};

export function Render({ holdData, holdId, userHasLogin, remaining }) {
    const navigate = useNavigate();
    const [clientSecret, setClientSecret] = useState(null);
    const [paymentIdStart, setPaymentIdStart] = useState(null);
    const [paymentState, setPaymentState] = useState("none");
    const [codeDiscount, setCodeDiscount] = useState("");
    const [errorCodeDiscount, setErrorCodeDiscount] = useState("");
    const [loadingDiscount, setLoadingDiscount] = useState(false);
    const [errorStripeError, setErrorStripeError] = useState(null);
    const [hold, setHold] = useState(holdData);
    const [stripePromise, setStripePromise] = useState(null);

    useEffect(() => {
        setHold(holdData);
    }, [holdData]);

    const URL_ORIGIN = process.env.NODE_ENV === "development"
        ? process.env.REACT_APP_API_URL_DEV
        : process.env.REACT_APP_API_URL;

    const subtotal = Number(hold?.pricing?.subtotal || 0);
    const serviceFee = Number(hold?.pricing?.service_fee || 0);
    const serviceFeePercen = Number(hold?.pricing?.service_fee_percent || 0);
    const discountTotal = Number(hold?.pricing?.discount_total || 0);
    const totalFinal = Number(hold?.pricing?.total || 0);

    const event = hold?.event;

    const nameParts = (event?.name || "").trim().split(" ");
    const firstWord = nameParts[0] || "";
    const restOfName = nameParts.slice(1).join(" ");

    useEffect(() => {
        const createIntent = async () => {
            if (!userHasLogin) return;
            if (!hold) return;
            if (hold.status !== "active") return;
            if (!holdId) return;
            if (clientSecret) return;

            try {
                const res = await api.post("/IntentPayment/Start", {
                    hold_id: holdId,
                    customer_id: userHasLogin.id,
                });

                if (res.data?.status === "completed" && res.data?.purchase_id) {
                    navigate(`/tickets/${res.data.purchase_id}`);
                    return;
                }

                setPaymentIdStart(res.data.payment_intent_id);
                setPaymentState(res.data.payment_state || "none");
                setClientSecret(res.data.clientSecret);
                setErrorStripeError(null);

                if (res.data?.pricing) {
                    setHold(prev => prev ? { ...prev, pricing: res.data.pricing } : prev);
                }
            } catch (err) {
                const friendlyMessage = getApiErrorMessage(
                    err,
                    "No pudimos preparar tu pago. Recarga la página e inténtalo nuevamente."
                );
                setErrorStripeError(friendlyMessage);
                console.error("Error creando PaymentIntent:", friendlyMessage);
            }
        };

        createIntent();
    }, [userHasLogin, holdId, hold?.status, clientSecret, errorStripeError]);

    useEffect(() => {
        const run = async () => {
            const eventId = hold?.event?.id || hold?.event_id;
            if (!eventId) return;

            try {
                const res = await api.get(`/Event/${eventId}/StripeKeySet`);
                const pk = res.data?.stripeKeySet?.publishableKey;

                if (!pk) {
                    console.error("Este evento no tiene publishableKey configurada");
                    return;
                }

                setStripePromise(loadStripe(pk));
            } catch (e) {
                console.error("Error cargando publishable key por evento:", e);
            }
        };

        run();
    }, [hold?.event?.id, hold?.event_id]);

    const handleApplyDiscount = async () => {
        setErrorCodeDiscount("");

        if (!codeDiscount.trim()) {
            setErrorCodeDiscount("Ingresa un código");
            return;
        }

        setLoadingDiscount(true);
        try {
            await api.post(`/Event/Holds/${holdId}/ApplyDiscount`, {
                code: codeDiscount.trim(),
                customer_id: userHasLogin.id,
            });

            const refreshed = await api.get(`/Event/Holds/${holdId}`);
            setHold(refreshed.data);
            setClientSecret(null);
            setErrorCodeDiscount("");
            toast.success("Cupón aplicado");
        } catch (error) {
            setErrorCodeDiscount(
                getApiErrorMessage(error, "Error aplicando cupón")
            );
        } finally {
            setLoadingDiscount(false);
        }
    };

    if (errorStripeError) return <MessageError title="Error al preparar el pago" description={errorStripeError} />;

    if (!clientSecret) return <Loading text="Preparando pago, un momento..." />;

    const options = {
        clientSecret,
        appearance,
        locale: "es-419",
        paymentMethodCreation: "manual",
    };

    const formatMMSS = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    return (
        <div className="bg-[#0f0e17] min-h-screen flex flex-col">
            <div className="max-w-6xl mx-auto p-6 lg:p-12 grid lg:grid-cols-2 gap-10">
                <div className="relative w-full rounded-xl overflow-hidden shadow-lg">
                    {event?.event_images?.map((img) => {
                        if (!img.is_primary) return null;
                        return (
                            <img
                                key={img.id}
                                src={`${URL_ORIGIN}/image-proxy?id=${img.id}`}
                                alt={img.image_name || "Event image"}
                                className="w-full h-full object-cover"
                            />
                        );
                    })}

                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-end p-6 text-white">
                        <h2 className="detail-title font-display text-2xl md:text-4xl font-bold leading-tight mb-4">
                            {firstWord}
                            {restOfName && (
                                <>
                                    <br />
                                    <span className="text-transparent bg-clip-text gradient-monoparty-text">
                                        {restOfName}
                                    </span>
                                </>
                            )}
                        </h2>

                        <div className="flex flex-col md:flex-row gap-4 text-sm mt-4">
                            <div className="flex items-center gap-2">
                                <FaMapMarkerAlt />
                                {event?.location}
                            </div>
                            <div className="flex items-center gap-2">
                                <FaCalendarAlt />
                                {event?.event_date
                                    ? moment(event.event_date)
                                        .utcOffset(0, true)
                                        .format("dddd, D [de] MMMM [del] YYYY")
                                    : ""}
                            </div>
                            <div className="flex items-center gap-2 capitalize">
                                <FaClock />
                                {event?.event_date
                                    ? moment(event.event_date).utcOffset(0, true).format("hh:mm a")
                                    : ""}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-[#16151c] border border-white/10 rounded-xl shadow-xl p-8">
                    <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-2">
                        <FaTicketAlt className="text-brand" />
                        Detalle de Compra
                    </h2>

                    <div className="mt-3 mb-5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300">
                        <div className="flex items-center justify-between">
                            <div className="font-semibold">Tiempo restante para pagar</div>
                            <div className="text-2xl font-bold tabular-nums">{formatMMSS(remaining)}</div>
                        </div>
                        <div className="text-sm mt-1">
                            Después de este tiempo, los boletos se liberan automáticamente.
                        </div>
                    </div>

                    <div className="space-y-4">
                        {(hold?.groups || []).map((g, idx) => (
                            <div key={idx} className="flex justify-between items-center pb-3 border-b border-white/5 last:border-0">
                                <div>
                                    <p className="text-white font-medium">
                                        {g.name_category}
                                        <span className="text-gray-400 ml-1">(x{g.quantity})</span>
                                    </p>
                                    <p className="text-gray-400 text-sm font-mono">
                                        {currencyFormatter(Number(g.unit_price))} c/u
                                    </p>
                                </div>

                                <div className="text-right text-white font-semibold font-mono">
                                    {currencyFormatter(Number(g.line_total))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {discountTotal > 0 && hold?.discount?.type !== "general" ? (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-300 rounded-lg">
                            <PiTicketDuotone size={18} className="text-green-600 shrink-0" />
                            <span className="text-green-800 font-medium text-sm">
                                Descuento <strong>{hold?.discount?.code}</strong> aplicado automáticamente
                            </span>
                        </div>
                    ) : discountTotal > 0 && hold?.discount?.type === "general" ? (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-300 rounded-lg">
                            <PiTicketDuotone size={18} className="text-green-600 shrink-0" />
                            <span className="text-green-800 font-medium text-sm">
                                Cupón <strong>{hold?.discount?.code}</strong> aplicado
                            </span>
                        </div>
                    ) : (
                        <>
                            <input
                                type="text"
                                placeholder="¿Tienes un código de descuento?"
                                className="w-full px-3 py-2 text-white bg-white/5 border border-white/20 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent placeholder-gray-500"
                                value={codeDiscount}
                                onChange={(e) => setCodeDiscount(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleApplyDiscount()}
                                disabled={loadingDiscount}
                            />

                            <button
                                className={`justify-center transition-all duration-200 ${loadingDiscount ? "thm-btn__disabled" : ""} event-one__btn thm-btn w-full mt-4 py-3`}
                                onClick={handleApplyDiscount}
                                type="button"
                                disabled={loadingDiscount}
                            >
                                <span className="flex items-center justify-center">
                                    <PiTicketDuotone size={22} className="mr-2" />
                                    {loadingDiscount ? "Aplicando..." : "Aplicar"}
                                </span>
                            </button>

                            {errorCodeDiscount && (
                                <div className="text-red-600 text-center text-sm pt-2">
                                    {errorCodeDiscount}
                                </div>
                            )}
                        </>
                    )}

                    <div className="mt-6 border-t border-white/10 pt-4 space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-400">Subtotal</span>
                            <span className="text-right text-white font-semibold font-mono">
                                {currencyFormatter(subtotal)}
                            </span>
                        </div>

                        <div className="flex justify-between">
                            <span className="text-gray-400">Servicio ({serviceFeePercen}%)</span>
                            <span className="text-right text-white font-semibold font-mono">
                                {currencyFormatter(serviceFee)}
                            </span>
                        </div>

                        {discountTotal > 0 && (
                            <div className="flex justify-between">
                                <span className="text-green-400 font-semibold">
                                    Descuento{hold?.discount?.code ? ` (${hold.discount.code})` : ""}
                                </span>
                                <span className="text-green-400 font-semibold font-mono">
                                    -{currencyFormatter(discountTotal)}
                                </span>
                            </div>
                        )}

                        <div className="flex justify-between text-lg font-bold mt-4 pt-3 border-t border-white/10">
                            <span className="text-brand">Total</span>
                            <span className="text-brand font-mono">
                                {currencyFormatter(totalFinal)}
                            </span>
                        </div>
                    </div>

                    <div className="mt-6">
                        {stripePromise ? (
                            <Elements key={clientSecret} stripe={stripePromise} options={options}>
                                <CheckoutForm
                                    holdId={holdId}
                                    userHasLogin={userHasLogin}
                                    remaining={remaining}
                                    paymentIdStart={paymentIdStart}
                                    paymentState={paymentState}
                                    clientSecret={clientSecret}
                                    onRequireNewPI={() => {
                                        setClientSecret(null);
                                        setPaymentIdStart(null);
                                    }}
                                />
                            </Elements>
                        ) : (
                            <Loading text="Cargando configuración de pago..." />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Payment() {
    const { holdId } = useParams();
    const [holdData, setHoldData] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [userHasLogin, setUserHasLogin] = useState(undefined);
    const [dataFraudAttempts, setDataFraudAttempts] = useState(null);
    const [error, setError] = useState(false);
    const [remaining, setRemaining] = useState(holdData?.remaining_seconds ?? 0);

    const { userLogged } = useResource();

    useEffect(() => {
        setRemaining(holdData?.remaining_seconds ?? 0);
    }, [holdData]);

    useEffect(() => {
        if (!remaining) return;
        const id = setInterval(() => {
            setRemaining((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(id);
    }, [remaining]);

    useEffect(() => {
        if (!holdId) return;
        if (remaining !== 0) return;

        api.get(`/Event/Holds/${holdId}`)
            .then((r) => setHoldData(r.data))
            .catch(() => { });
    }, [remaining, holdId]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const holdRes = await api.get(`/Event/Holds/${holdId}`);
                setHoldData(holdRes.data);

                const userLoggedResponse = await getUser();

                if (userLoggedResponse?.status === 401) {
                    setUserHasLogin(null);
                    setAuthChecked(true);
                    setDataFraudAttempts([]);
                    return;
                }

                setUserHasLogin(userLoggedResponse);
                setAuthChecked(true);

                const fraudAttempts = await api.get(`/IntentPayment/Fraud?id=${userLoggedResponse.id}`);
                setDataFraudAttempts(fraudAttempts.data);
            } catch (err) {
                console.error("Error cargando datos del pago:", err);
                setError(true);
            }
        };

        fetchAllData();
    }, [holdId, userLogged]);

    if (error) {
        return (
            <MessageError
                icon={FaExclamationCircle}
                title="Error al cargar el pago"
                description="Por favor, intenta de nuevo."
            />
        );
    }

    if (!holdData || !dataFraudAttempts) {
        return <SkeletonPayment />;
    }

    if (!authChecked) return <Loading text="Verificando sesión..." />;

    if (userHasLogin === null) {
        return (
            <MessageError
                icon={FaTicketAlt}
                title="Inicia sesión para continuar"
                description={<>Necesitas una cuenta para completar tu compra y recibir tus boletos. Usa el botón <strong className="text-white">Iniciar Sesión</strong> en la esquina superior derecha.</>}
            />
        );
    }

    if (dataFraudAttempts?.customer?.is_blocked) {
        return (
            <MessageError
                icon={FaLock}
                title="Cuenta bloqueada temporalmente"
                description={`Motivo: ${dataFraudAttempts.customer.blocked_reason}. Por tu seguridad, contacta a nuestro equipo de soporte para reactivar el acceso.`}
            />
        );
    }

    if (!dataFraudAttempts) return <Loading text="Cargando validaciones..." />;

    const isExpired =
        holdData.status === "expired" ||
        (holdData.status === "active" && remaining <= 0);

    if (isExpired) {
        return (
            <MessageError
                icon={FaClock}
                iconColor="amber"
                title="Se terminó tu tiempo de compra"
                description="Reservamos tus boletos por 5 minutos. El tiempo se agotó y los asientos se liberaron. Por favor, vuelve a intentarlo."
            />
        );
    }

    if (holdData.status === "completed") {
        return (
            <MessageError
                icon={FaCheckCircle}
                iconColor="green"
                title="¡Compra completada!"
                description="Tu compra ya fue procesada. Espera unos segundos estamos generando tus boletos..."
            />
        );
    }

    return <Render holdData={holdData} holdId={holdId} userHasLogin={userHasLogin} remaining={remaining} />;
}